import { Button, Callout } from "@blueprintjs/core";
import { ButtonWithDialog, ItemStatus, Loading } from "@focus/focusUI";
import { FocusFormBuilder, type FormData } from "@focus/formComponents";
import React, { useEffect, useMemo, useState } from "react";
import { QuestionnaireForm } from "../components/QuestionnaireForm";
import ErrorsCollapse from "../components/Settings/OperatorConfig/ErrorsCollapse";
import "../components/Settings/OperatorConfig/OperatorConfig.scss";
import { OperatorConfigHeader } from "../components/Settings/OperatorConfig/OperatorConfigHeader";
import { useMe } from "../hooks/useMe";
import { ROLES, useRoles } from "../hooks/useRoles";
import { useSystemConfig } from "../hooks/useSystemConfig";
import { sendQuestionnarie } from "../services/questionnaires";
import {
  buildObjectSchemaFromArrayItems,
  toFocusFormErrors,
} from "../utils/forms";
import {
  buildNewConfig,
  deepEqual,
  transformSchemaByRole,
} from "../utils/operatorConfig";
import S from "../utils/schemas/SatelliteQuestionnaire.json";
import { sortAlphabetically } from "../utils/sorting";
import { processCsv } from "../utils/upload";

const ROLE_PRIORITY = ["admin", "org-admin", "user"] as const;
const ASSISTED_FILL_DIALOG_STYLE = {
  width: "75%",
  minWidth: "40%",
  maxHeight: "calc(100vh - 60px)",
  overflow: "hidden",
} satisfies React.CSSProperties;

type Role = (typeof ROLE_PRIORITY)[number];
type ErrorsMap = Record<string, string>;

const normalizeServerErrorPath = (path: string) =>
  path
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean)
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))
    .join(".");

const normalizeServerErrors = (
  serverErrors?: Record<string, string> | null,
): ErrorsMap => {
  if (!serverErrors) return {};

  return Object.fromEntries(
    Object.entries(serverErrors).map(([path, message]) => [
      normalizeServerErrorPath(path),
      message,
    ]),
  );
};

function resolveEffectiveRole(userRoles: Role[]): Role {
  for (const role of ROLE_PRIORITY) {
    if (userRoles.includes(role)) {
      return role;
    }
  }
  throw new Error("User has no valid roles");
}

export const OperatorConfigContainer: React.FC = () => {
  const { roles } = useRoles();
  const me = useMe();
  const {
    schema,
    currentConfig,
    currentConfigOperatorName,
    loadConfig,
    saveConfig,
    loadSchema,
    getRequestStatus,
    getRequestFieldErrors,
  } = useSystemConfig();
  const [selectedOperator, setSelectedOperator] = useState<string | null>(null);
  const [initialData, setInitialData] = useState<FormData | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [errors, setErrors] = useState<ErrorsMap>({});

  const satelliteQuestionnaireSchema = useMemo(
    () => buildObjectSchemaFromArrayItems(S),
    [],
  );
  // Default form data for the satellite questionnaire, built from its schema
  const defaultQuestionnaireForm = useMemo(
    () => buildNewConfig(satelliteQuestionnaireSchema),
    [satelliteQuestionnaireSchema],
  );

  /* =======================
     Validation
     ======================= */

  const validate = (data: FormData | null, schema): ErrorsMap => {
    const errors: ErrorsMap = {};

    const resolveRef = (ref?: string, rootSchema?: any) => {
      if (!ref || !rootSchema?.definitions) return undefined;
      if (ref.startsWith("#/definitions/")) {
        const k = ref.split("/").pop()!;
        return rootSchema.definitions[k];
      }
      return undefined;
    };

    const walk = (
      obj: any,
      schemaNode: any,
      path: string[] = [],
      parentRequired?: string[],
    ) => {
      if (!schemaNode) return;

      // resolver $ref
      const node = schemaNode.$ref
        ? (resolveRef(schemaNode.$ref, schema) ?? schemaNode)
        : schemaNode;

      // --- check required ---
      if (parentRequired && path.length > 0) {
        const lastKey = path[path.length - 1];
        if (parentRequired.includes(lastKey)) {
          if (obj === undefined || obj === "") {
            errors[path.join(".")] = "Required field";
          }
        }
      }

      // --- descend objects ---
      if (node.type === "object" && node.properties) {
        Object.entries(node.properties).forEach(([key, childSchema]) => {
          walk(obj?.[key], childSchema, [...path, key], node.required);
        });
      }

      // --- descend arrays ---
      else if (node.type === "array") {
        const arr = Array.isArray(obj) ? obj : [];

        // ⬇️ VALIDAR minItems / maxItems
        if (node.minItems !== undefined && arr.length < node.minItems) {
          errors[path.join(".")] = `At least ${node.minItems} item(s) required`;
        }

        if (node.maxItems !== undefined && arr.length > node.maxItems) {
          errors[path.join(".")] = `At most ${node.maxItems} item(s) allowed`;
        }

        // recorrer items
        arr.forEach((item, idx) => {
          walk(item, node.items, [...path, String(idx)], node.items?.required);
        });
      }
    };

    walk(data, schema, [], schema?.required);
    return errors;
  };

  /* =======================
     Permissions
     ======================= */

  const isApiRole = roles.includes("api");

  /* =======================
     Derived state
     ======================= */

  const requestStatus = getRequestStatus(["getConfig", "getSchema"]);
  const saveConfigFieldErrors = getRequestFieldErrors("setConfig");
  const isLoading = requestStatus === ItemStatus.PENDING;

  /* =======================
     Handlers
     ======================= */
  const handleReset = () => {
    setFormData(currentConfig || {});
    setInitialData(currentConfig || {});
  };

  const hasErrors = Object.keys(errors).length > 0;
  const hasChanges = useMemo(() => {
    if (!initialData || !formData) return false;
    return !deepEqual(initialData, formData);
  }, [initialData, formData]);

  const formErrors = useMemo(() => toFocusFormErrors(errors), [errors]);
  const sortedOperators = useMemo(
    () => sortAlphabetically(me?.organizations ?? []),
    [me?.organizations],
  );

  const handleSave = () => {
    if (!selectedOperator || !formData || !schema || hasErrors) return;
    saveConfig(selectedOperator, formData);
  };

  const handleExport = () => {
    if (!formData) {
      alert("No configuration available to export.");
      return;
    }

    const blob = new Blob([JSON.stringify(formData, null, 2)], {
      type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `system-config-${new Date()
      .toISOString()
      .replace(/[:.]/g, "-")}.json`;

    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCreateNewConfig = () => {
    const emptyConfig = buildNewConfig(schema);
    setFormData(emptyConfig);
    setInitialData(emptyConfig);
  };

  const handleUploadCsv = async (file: File) => {
    const rows = await processCsv(file);
    return { satelliteList: rows };
  };
  /* =======================
     Effects
     ======================= */

  useEffect(() => {
    if (!selectedOperator) return;
    setFormData(null);
    loadSchema(selectedOperator);
    loadConfig(selectedOperator);
  }, [selectedOperator]);

  useEffect(() => {
    if (
      currentConfig &&
      selectedOperator &&
      currentConfigOperatorName === selectedOperator
    ) {
      setFormData(currentConfig);
      setInitialData(currentConfig);
    }
  }, [currentConfig, currentConfigOperatorName, selectedOperator]);

  useEffect(() => {
    const errs = validate(formData, schema);
    setErrors(errs);
  }, [formData, schema]);

  useEffect(() => {
    const serverErrors = normalizeServerErrors(saveConfigFieldErrors);
    if (Object.keys(serverErrors).length === 0) return;

    setErrors((currentErrors) => ({
      ...currentErrors,
      ...serverErrors,
    }));
  }, [saveConfigFieldErrors]);

  /* =======================
     Guards
     ======================= */
  if (isApiRole) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <div className="max-w-md rounded-2xl border border-red-200 bg-red-50 p-6">
          <div className="text-xs font-semibold uppercase text-red-600">
            Access
          </div>
          <h2 className="mt-2 text-xl font-bold text-red-900">Access denied</h2>
          <p className="mt-2 text-sm font-medium text-red-700">
            API users cannot edit operator configuration from this view.
          </p>
        </div>
      </div>
    );
  }

  const effectiveRole = resolveEffectiveRole(roles);
  const transformed = transformSchemaByRole(schema, effectiveRole);

  /* =======================
     Render
     ======================= */
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <OperatorConfigHeader
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasErrors={hasErrors}
        handleExport={handleExport}
        handleReset={handleReset}
        handleSave={handleSave}
        onOperatorChange={setSelectedOperator}
        operators={sortedOperators}
        selectedOperator={selectedOperator}
        showActionButtons={Boolean(
          selectedOperator &&
          (currentConfig || formData) &&
          effectiveRole !== "user",
        )}
        title={schema?.title}
      />

      <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto p-4">
        {!isLoading && selectedOperator && schema?.properties && formData && (
          <ErrorsCollapse errors={errors} />
        )}

        {isLoading && <Loading />}

        {!isLoading && !selectedOperator && (
          <Callout
            className="rounded-xl border border-slate-200 bg-white"
            intent="primary"
            title="No operator selected"
          >
            Please select an operator to view or edit its configuration.
          </Callout>
        )}

        {!isLoading && selectedOperator && schema?.properties && formData && (
          <div className="flex h-full min-h-0 flex-col">
            <div className="basis-[95%] min-h-0 overflow-auto focusoc-form-shell">
              <FocusFormBuilder
                schema={transformed}
                uiSchema={undefined}
                formData={formData}
                errors={formErrors}
                onChange={setFormData}
              />
            </div>
            {
              // TODO IMPLEMENTAR ASISTED FILL PARA SATELLITE CONFIG CUANDO API ESTE LISTA
              // TODO POR AHORA SOLO MOSTRAMOS EL BOTON SI EL USUARIO ES ADMIN
            }
            {(roles.includes(ROLES.ADMIN) ||
              roles.includes(ROLES.ORG_ADMIN)) && (
              <div className="basis-[5%] shrink-0">
                <ButtonWithDialog
                  className="assisted-fill-dialog focusoc-theme-primary inline-flex min-h-10 cursor-pointer items-center rounded-lg! border px-4! text-sm! font-bold! shadow-sm! transition"
                  intent={"primary"}
                  text="Add / remove satellites"
                  tooltipText="Click to open a satellite assisted form help "
                  defaultContent={{
                    email: "",
                    name: "",
                    organization: "",
                    service: "satellite-add",
                    formDetails: defaultQuestionnaireForm,
                  }}
                  enableApplySaveButtonFooters={{
                    apply: { disabled: true, hidden: true },
                    save: { disabled: true, hidden: true },
                    cancel: { disabled: true, hidden: true },
                    close: { disabled: false, hidden: true },
                  }}
                  style={ASSISTED_FILL_DIALOG_STYLE}
                  headerTitle="Satellite assisted form"
                  form={({ content }) => {
                    return (
                      <div className="operator-config-questionnaire-dialog-content">
                        <QuestionnaireForm
                          handleUpload={handleUploadCsv}
                          title="Need assistance with satellite configuration?"
                          handleSubmit={sendQuestionnarie}
                          reason="Please fill out the form to assist with your satellite inquiry."
                          initialData={content}
                          contactServiceOptions={[
                            {
                              label: "Add satellite",
                              value: "satellite-add",
                            },
                            {
                              label: "Remove satellite",
                              value: "satellite-remove",
                            },
                            {
                              label: "Edit satellite",
                              value: "satellite-edit",
                            },
                          ]}
                          validate={validate}
                          schema={satelliteQuestionnaireSchema}
                          confirmOnSubmit
                          uploadButton={true}
                        />
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </div>
        )}

        {!isLoading && selectedOperator && schema?.properties && !formData && (
          <Callout
            className="rounded-xl border border-slate-200 bg-white"
            intent="primary"
            title="No configuration found for this operator."
          >
            <p>
              No saved configuration was found for <b>{selectedOperator}</b>.
              {roles.includes(ROLES.ADMIN)
                ? " You can create a new one."
                : " You do not have permission to create one."}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              {roles.includes(ROLES.ADMIN) && (
                <Button
                  icon="plus"
                  intent="primary"
                  onClick={handleCreateNewConfig}
                  text="Create new configuration"
                />
              )}
            </div>
          </Callout>
        )}
      </div>
    </div>
  );
};
